import React from "react";
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import { MdAdminPanelSettings, MdBadge, MdWavingHand } from "react-icons/md";

import { getSecureLocalItem } from "utils/secureSessionStorage";
import kolBanner from "../../assets/img/kol-banner.png";
const DAILY_QUOTES = {
  Monday: "A fresh week brings new opportunities to learn, grow, and achieve.",
  Tuesday:
    "Stay consistent, keep improving, and let your hard work speak for itself.",
  Wednesday:
    "You're halfway there; keep your focus strong and your momentum going.",
  Thursday:
    "Every challenge is an opportunity to grow and every effort brings you closer to success.",
  Friday:
    "Finish the week with pride, celebrate your progress, and appreciate the journey.",
  Saturday: "Make every moment count, keep learning, and never stop growing.",
  Sunday:
    "Take time to recharge, reflect on your achievements, and prepare for a brighter tomorrow.",
};

const HeaderBanner = ({ user, userRole, attendanceCount }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const role = getSecureLocalItem("role") || userRole || "admin";
  const userRaw = getSecureLocalItem("user");
  const parsedUser = userRaw ? JSON.parse(userRaw) : null;
  const currentUser = user || parsedUser;

  const emailUsername = currentUser?.email
    ? currentUser.email.split("@")[0]
    : "";
  const fullName = currentUser?.first_name
    ? `${currentUser.first_name} `
    : currentUser?.name || "User";

  const getUserName = () => {
    if (role === "SUPER_ADMIN") return "Super Admin";
    if (role === "Company") return emailUsername || "User";
    return fullName || "User";
  };

  const userName = getUserName();
  const isEmployeeRole = role?.toLowerCase() === "employee";

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const currentDateFormatted = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  const todayWeekday = new Date().toLocaleDateString("en-US", {
    weekday: "long",
  });
  const dailyQuote = DAILY_QUOTES[todayWeekday] || "";

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: -15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      sx={{
        mb: isEmployeeRole ? 0 : 4,
        p: { xs: 3, sm: 4 },
        borderRadius: 1,
        position: "relative",
        overflow: "hidden",
        background: isDark
          ? "linear-gradient(90deg, rgba(245, 158, 11, 0.16) 0%, rgba(17, 24, 39, 0.95) 35%, rgba(30, 41, 59, 0.95) 100%)"
          : "linear-gradient(90deg, #fef9e7 0%, #ffffff 35%, #f8fafc 100%)",
        border: `1px solid ${
          isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"
        }`,
        boxShadow: isDark
          ? "0 10px 30px rgba(0, 0, 0, 0.35)"
          : "0 10px 30px rgba(15, 23, 42, 0.05)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: -50,
          left: 100,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0) 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Kolkata landscape — right side, employee only, faded */}
      {isEmployeeRole && (
        <Box
          component="img"
          src={kolBanner}
          alt="Kolkata landscape"
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: { xs: "55%", sm: "50%", md: "45%" },
            objectFit: "cover",
            objectPosition: "center right",
            opacity: isDark ? 0.35 : 0.3,
            pointerEvents: "none",
            zIndex: 0,
            userSelect: "none",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, #000 25%, #000 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, #000 25%, #000 100%)",
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left Side: Avatar & Greeting */}
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <MdWavingHand color="#f59e0b" /> {getGreeting()}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDark ? "#475569" : "#cbd5e1" }}
              >
                •
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "text.secondary" }}
              >
                {currentDateFormatted}
              </Typography>
            </Stack>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.02em",
                fontSize: { xs: "1.4rem", sm: "1.85rem" },
                background: isDark
                  ? "linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)"
                  : "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Welcome back, {userName}!
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
            >
              <Chip
                icon={
                  isEmployeeRole ? (
                    <MdBadge style={{ color: "#10b981" }} />
                  ) : (
                    <MdAdminPanelSettings style={{ color: "primary.main" }} />
                  )
                }
                label={
                  isEmployeeRole
                    ? "Employee Workspace"
                    : "Company Administrator"
                }
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  backgroundColor: isEmployeeRole
                    ? isDark
                      ? alpha("#10b981", 0.15)
                      : "#d1fae5"
                    : isDark
                    ? alpha("#f59e0b", 0.15)
                    : "#fef3c7",
                  color: isEmployeeRole ? "#059669" : "#b45309",
                  border: `1px solid ${
                    isEmployeeRole
                      ? isDark
                        ? alpha("#10b981", 0.3)
                        : "#a7f3d0"
                      : isDark
                      ? alpha("#f59e0b", 0.3)
                      : "#fde68a"
                  }`,
                  borderRadius: "8px",
                }}
              />

              {/* {typeof attendanceCount === "number" && attendanceCount > 0 && ( ... )} */}
            </Stack>

            {/* Daily quote — employee only */}
            {isEmployeeRole && dailyQuote && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1.25,
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: { xs: "0.8rem", sm: "0.88rem" },
                  color: isDark ? alpha("#cbd5e1", 0.85) : "#475569",
                  maxWidth: 620,
                  lineHeight: 1.45,
                }}
              >
                “{dailyQuote}”
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Right Side: Quick Action Buttons */}
        {/* <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: "stretch", md: "center" } }}>
          <Button
            variant="outlined"
            size="medium"
            startIcon={<MdPerson />}
            onClick={() => navigate(isEmployeeRole ? "/company/employee/profile" : "/company/profile")}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
              py: 1,
              borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "#cbd5e1",
              color: isDark ? "#e2e8f0" : "#334155",
              "&:hover": {
                borderColor: "#f59e0b",
                backgroundColor: alpha("#f59e0b", 0.08),
              },
            }}
          >
            My Profile
          </Button>

          <Button
            variant="contained"
            size="medium"
            startIcon={<MdChat />}
            onClick={() => navigate("/company/chat")}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 700,
              px: 2.5,
              py: 1,
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              boxShadow: "0 4px 14px rgba(245, 158, 11, 0.35)",
              color: "#ffffff",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                boxShadow: "0 6px 18px rgba(245, 158, 11, 0.45)",
              },
            }}
          >
            AI Assistant
          </Button>
        </Stack> */}
      </Box>
    </Box>
  );
};

export default HeaderBanner;
